<template>
    <div class="videoPreviewCanvas" @click.self="$emit('clickOutside')">
        <!-- .vpc-stage centers whatever space it's given (fullscreen included); .vpc-frame's own
             size is computed in JS to match the media's real ratio - see recomputeFrameSize. -->
        <div class="vpc-stage" ref="stage" @mousemove="onStageMouseMove" @mouseleave="onStageMouseLeave" @mousedown="onStagePointerDown" @click.self="onStageClick">
            <div class="vpc-frame" ref="frame" :style="frameStyle">
                <canvas ref="canvas" :width="displayCanvasSize.width" :height="displayCanvasSize.height"></canvas>
                <!-- Hidden only while the frame is source-shaped (Remplir cropping, see
                     cropWorkspaceSize): overlays are positioned in fractions of the *output* frame,
                     so on a source-shaped frame they'd sit somewhere they'll never actually be. -->
                <OverlayCanvas
                    v-if="!cropWorkspaceSize"
                    ref="overlayCanvas"
                    class="vpc-overlay-layer"
                    :overlays="overlays"
                    :canvasSize="displayCanvasSize"
                    :currentMs="currentMs"
                    @update:overlay-transform="e => $emit('update:overlay-transform', e)"
                    @select="id => $emit('select-overlay', id)"
                    @live-transform="onLiveOverlayTransform"
                    @start-edit-text="startEditText"
                    @multi-selection="onMultiSelectionChanged"
                />

                <!-- Double-clicking a text/hashtag/mention/place overlay floats this directly on
                     top of it instead of trying to edit the underlying fabric object in place -
                     see OverlayCanvas.vue's own onCanvasDoubleClick for why. -->
                <input
                    v-if="textEditor"
                    ref="textEditorInput"
                    type="text"
                    class="vpc-text-editor"
                    :style="textEditor.style"
                    v-model="textEditorValue"
                    @keydown.enter.prevent="commitTextEdit"
                    @keydown.esc.prevent="cancelTextEdit"
                    @blur="commitTextEdit"
                >

                <!-- Shown whenever 2+ overlays are selected on the canvas (a rubber-band drag, a
                     click on any member of an existing group auto-expanding to the whole group,
                     or shift/ctrl-click) - see OverlayCanvas.vue's emitMultiSelectionToolbar. -->
                <div v-if="multiSelection" class="vpc-multi-toolbar" :style="multiSelection.style">
                    <div class="vpc-multi-btn" :title="$t('dash.post-creator.media-editor.multi-toolbar.duplicate')" @click="$emit('duplicate-selection', multiSelection.overlayIds)">
                        <i class="fa-solid fa-clone"></i>
                    </div>
                    <div
                        class="vpc-multi-btn"
                        :title="$t(multiSelection.isCompleteGroup ? 'dash.post-creator.media-editor.multi-toolbar.ungroup' : 'dash.post-creator.media-editor.multi-toolbar.group')"
                        @click="onToggleGroupClick"
                    >
                        <i class="fa-solid" :class="multiSelection.isCompleteGroup ? 'fa-object-ungroup' : 'fa-object-group'"></i>
                    </div>
                    <div class="vpc-multi-btn" :title="$t('dash.post-creator.media-editor.multi-toolbar.remove')" @click="$emit('remove-selection', multiSelection.overlayIds)">
                        <i class="fa-solid fa-trash"></i>
                    </div>
                </div>

                <div
                    v-if="placementMode"
                    class="vpc-placement-catcher"
                    @click="onPlacementClick"
                    @mousemove="onPlacementMove"
                    @mouseleave="onPlacementLeave"
                >
                    <div class="vpc-guide vpc-guide-v" v-if="hoverSnap.x !== null" :style="{ left: (hoverSnap.x * 100) + '%' }"></div>
                    <div class="vpc-guide vpc-guide-h" v-if="hoverSnap.y !== null" :style="{ top: (hoverSnap.y * 100) + '%' }"></div>
                </div>
                <CropSelector
                    v-if="cropClip"
                    :clip="cropClip"
                    :canvasSize="displayCanvasSize"
                    :outputRatio="canvasSize.width / canvasSize.height"
                    @update-crop-rect="rect => $emit('update-crop-rect', rect)"
                    @crop-commit="$emit('crop-commit')"
                    @drag-start="onCropDragStart"
                    @drag-end="isDraggingCrop = false"
                />
            </div>

            <!-- Fullscreen-only transport - the editor's own controls (Timeline.vue) aren't
                 visible once .vpc-stage fills the screen, so fullscreen needs its own minimal
                 play/pause + scrub, auto-hiding after a few seconds of no mouse movement like any
                 native video player (see fsControlsVisible/scheduleHideFsControls). -->
            <div v-if="isFullscreen" class="vpc-fs-controls" :class="{ visible: fsControlsVisible }">
                <div class="vpc-fs-play" @click="togglePlay"><i class="fa-solid" :class="playing ? 'fa-pause' : 'fa-play'"></i></div>
                <div class="vpc-fs-progress" ref="fsProgress" @mousedown="startFsScrub">
                    <div class="vpc-fs-progress-fill" :style="{ width: fsProgressPct + '%' }"></div>
                </div>
                <span class="vpc-fs-time">{{ formatTimecode(currentMs) }} / {{ formatTimecode(durationMs) }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import OverlayCanvas from './OverlayCanvas.vue'
import CropSelector from './CropSelector.vue'
import { TimelinePlayer } from '../engine/playback/TimelinePlayer.js'
import { computeSnap } from '../engine/snapGuides.js'
import { computeTimelineDurationMs } from '../engine/timelineModel.js'
import { capResolution } from '../engine/export/qualitySettings.js'
import { formatTimecode } from '../engine/time.js'

// How long the fullscreen transport stays visible after the last mouse movement, matching the
// auto-hide behavior of a native video player's own controls.
const FS_CONTROLS_HIDE_MS = 2500

export default {
    name: 'VideoPreviewCanvas',

    components: {
        OverlayCanvas,
        CropSelector
    },

    props: {
        timeline: {
            type: Object,
            required: true
        },
        overlays: {
            type: Array,
            default: () => []
        },
        resolveSource: {
            type: Function,
            required: true
        },
        placementMode: {
            type: Boolean,
            default: false
        },
        cropClip: {
            type: Object,
            default: null
        },
        // The overlay currently being repositioned (if any) must not snap against its own
        // pre-move position - see startReposition in MediaEditor.vue.
        excludeOverlayId: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            currentMs: 0,
            playing: false,
            player: null,
            hoverSnap: { x: null, y: null },
            // Resizing .vpc-frame mid-gesture (e.g. OverlayConfigPanel closing because the drag
            // just cleared a single selection) leaves fabric's own mouse-to-canvas offset stale
            // for the rest of that gesture, corrupting rubber-band selection/dragging. Pinned
            // while any pointer is down anywhere on the stage - see onStagePointerDown/Up.
            isPointerDown: false,
            frameSize: { width: 0, height: 0 },
            isFullscreen: false,
            fsControlsVisible: false,
            fsScrubbing: false,
            fsHideTimeout: null,
            // { overlayId, field, style } while a double-click-to-edit input is open - see
            // startEditText/OverlayCanvas.vue's onCanvasDoubleClick.
            textEditor: null,
            textEditorValue: '',
            // { overlayIds, style, isCompleteGroup, groupId } | null - see OverlayCanvas.vue's
            // emitMultiSelectionToolbar/onMultiSelectionChanged below.
            multiSelection: null,
            // Snapshot of {width, height} taken the instant cropPreviewActive turns on - null
            // whenever it's off. See displayCanvasSize for why.
            frozenCanvasSize: null,
            // True only between a CropSelector drag's mousedown and mouseup (see the @drag-start/
            // @drag-end listeners on <CropSelector> above) - see cropPreviewActive for why this
            // matters.
            isDraggingCrop: false
        }
    },

    computed: {
        // Pinned during a gesture (see recomputeFrameSize/isPointerDown) - just formats whatever
        // frameSize currently holds.
        frameStyle() {
            if (!this.frameSize.width || !this.frameSize.height) return {}
            return { width: this.frameSize.width + 'px', height: this.frameSize.height + 'px' }
        },
        durationMs() {
            return computeTimelineDurationMs(this.timeline)
        },
        fsProgressPct() {
            return (this.currentMs / (this.durationMs || 1)) * 100
        },
        // A plain inline object literal in the template (`:canvasSize="{ width: ..., height: ... }"`)
        // gets recreated on every single re-render of this component - which happens on every video
        // frame (currentMs updates every tick during playback) and on every placement-mode mousemove
        // (hoverSnap). OverlayCanvas.vue's own `canvasSize` watcher is `deep: true` and reacts to
        // that brand-new-reference object as a real change every time, even though width/height never
        // actually differ - calling fabricCanvas.setDimensions() (which resets the whole canvas
        // backstore/context per the HTML5 canvas spec, even when reassigned to the same values) and a
        // full syncOverlays() dozens of times a second. A computed property only produces a new
        // object when timeline.canvas.width/height themselves actually change, so downstream watchers
        // stop firing on unrelated re-renders.
        canvasSize() {
            return { width: this.timeline.canvas.width, height: this.timeline.canvas.height }
        },
        // The crop tool needs the *full, uncropped* source on screen to make any sense (you can't
        // reposition into part of the source you can't see), and it needs it for the whole time the
        // tool is open - not just while dragging. CropSelector.vue's containBox places the selection
        // box assuming exactly this full-source contain-fit render, so the moment the canvas shows
        // the cropped result instead, the box is drawn in a coordinate system that isn't on screen
        // and simply stops matching the pixels behind it. Tying this to isDraggingCrop also made the
        // picture zoom out on every mousedown and back in on every mouseup, which reads as the fill
        // mode flipping by itself.
        //
        // What that costs - Ajuster/Remplir no longer changing the canvas render while the tool is
        // open - is covered by CropSelector.vue's own dashed result box (see its resultStyle), which
        // shows what each mode actually keeps without disturbing the reference view.
        cropPreviewActive() {
            return !!this.cropClip
        },
        // Which clip the player should draw uncropped - it reads this live through
        // getCropPreviewClipId, so no lifecycle ordering can leave the two disagreeing. Also watched
        // (below) purely to trigger a redraw: switching clip with the tool still open changes which
        // frame should be on screen without changing anything else the player would notice.
        cropPreviewClipId() {
            return this.cropPreviewActive ? this.cropClip.id : null
        },
        // The two fill modes are two different jobs, and the frame has to be shaped for whichever
        // one is on screen:
        //
        //   Remplir/cover - "pick which part of the media fills the frame". That's a selection job,
        //     so the frame takes the *source's* shape: the media fills it edge to edge at full size
        //     and the ratio-locked selection box moves over a completely static picture. Keeping the
        //     output shape here is what made this unusable - a 2.3:1 source in a 1:1 output became a
        //     thin strip of media inside a square frame, with a correspondingly tiny selection box.
        //     The box itself carries the format (it's locked to it), so nothing is lost.
        //
        //   Ajuster/contain - "show the whole media, letterboxed into the frame". Nothing is being
        //     selected (applyCropRatio resets the crop to the full source), so the frame keeps the
        //     *output* shape and simply is the preview of that. It has to: the output frame is larger
        //     than the media on both axes by construction there, so an overlay drawn at the media's
        //     scale always falls outside the frame and gets clipped - picking 1:1 or 9:16 on a
        //     source-shaped frame changed literally nothing on screen.
        //
        // See renderClipFrame for the other half of this: the space around the media stays plain
        // rather than filled with the blurred composite, which is what made this read as a rendered
        // result instead of a workspace.
        cropWorkspaceSize() {
            if (!this.cropPreviewActive) return null
            if ((this.cropClip.transform.fillMode || 'contain') !== 'cover') return null
            const { sourceWidth, sourceHeight } = this.cropClip
            if (!sourceWidth || !sourceHeight) return null
            // Capped like any other canvas - a 6000px source would otherwise allocate a 6000px
            // backing store for a preview a few hundred CSS pixels wide.
            return capResolution(sourceWidth, sourceHeight)
        },

        // frozenCanvasSize is belt and braces around a crop gesture. Dragging no longer resizes the
        // canvas at all (see MediaEditor.vue's onUpdateCropRect/commitCropRect), but a *stage*
        // resize landing mid-gesture - a side panel opening, the media-error banner appearing -
        // would still change the frame under a drag whose deltas were scaled against the frame it
        // started on (see CropSelector.vue's startDrag).
        displayCanvasSize() {
            return this.frozenCanvasSize || this.cropWorkspaceSize || this.canvasSize
        }
    },

    watch: {
        cropPreviewClipId() {
            this.scheduleCanvasRedraw()
        },
        // Keyed off the gesture, not off cropPreviewActive: the latter now stays true for the whole
        // time the crop tool is open, which would freeze the frame permanently and make the ratio
        // buttons look like they do nothing.
        isDraggingCrop(dragging) {
            // Whatever displayCanvasSize currently resolves to - in Remplir that's the
            // source-shaped workspace, and pinning the output canvas instead would snap the
            // frame back to the output shape the instant a drag started.
            this.frozenCanvasSize = dragging ? (this.cropWorkspaceSize || this.canvasSize) : null
        },
        // Closing the crop tool (or switching clip) mid-drag destroys CropSelector before it ever
        // emits drag-end, which would otherwise leave the frame frozen at that gesture's size for
        // the rest of the session.
        cropClip(clip) {
            if (!clip) this.isDraggingCrop = false
        },
        // The consolidated transport bar (play/pause icon) lives in Timeline.vue now, one level
        // up - it has no direct access to this component's own player, so this is how it learns
        // playback started/stopped (both from its own togglePlay() and from playback naturally
        // ending on its own, which only updates `playing` via onTimeUpdate below).
        playing(value) {
            this.$emit('playing-changed', value)
        },
        displayCanvasSize() {
            // $nextTick: watchers run before the render watcher patches the DOM, and opening the
            // crop tool also opens ClipToolPanel, which narrows the stage. Measuring now would size
            // the frame against the pre-panel width - too big for the stage it's meant to fit
            // inside, so .vpc-stage's overflow clips it and the picture reads as zoomed past its
            // own bounds until something else recomputes it.
            if (!this.isPointerDown) this.$nextTick(this.recomputeFrameSize)
            this.scheduleCanvasRedraw()
        }
    },

    mounted() {
        this.player = new TimelinePlayer({
            canvasEl: this.$refs.canvas,
            getTimeline: () => this.timeline,
            resolveSource: this.resolveSource,
            getOverlayCanvasEl: () => this.$refs.overlayCanvas && this.$refs.overlayCanvas.getCanvasElement(),
            getCropPreviewClipId: () => this.cropPreviewClipId,
            onTimeUpdate: (ms) => {
                this.currentMs = ms
                this.playing = this.player && this.player.playing
                this.$emit('time-update', ms)
            },
            onMediaError: (fileId) => this.$emit('media-error', fileId)
        })
        // Deferred rather than an immediate player.refresh(): MediaEditor.vue opens the crop tool on
        // startup (see its created hook), so cropClip is already set on the very first render and no
        // watcher ever fires for that initial value. Going through the scheduler means the first
        // draw happens once the canvas element carries its real size, with the crop preview already
        // in effect - the player reads that live now, so it can't disagree either way.
        this.scheduleCanvasRedraw()
        // Same reason the ResizeObserver below can't be the only source: the very first frame has to
        // be sized once the stage has actually been laid out with whatever panels open alongside it.
        this.$nextTick(this.recomputeFrameSize)
        document.addEventListener('fullscreenchange', this.onFullscreenChange)

        // A side panel toggling won't fire window 'resize' - this catches it instead.
        this.stageResizeObserver = new ResizeObserver(() => {
            if (!this.isPointerDown) this.recomputeFrameSize()
        })
        this.stageResizeObserver.observe(this.$refs.stage)
        // Catches any resize that landed mid-gesture (frozen while isPointerDown) as soon as the
        // gesture ends, even if released outside .vpc-stage.
        document.addEventListener('mouseup', this.onDocumentPointerUp)
    },

    beforeDestroy() {
        if (this.player) this.player.destroy()
        document.removeEventListener('fullscreenchange', this.onFullscreenChange)
        document.removeEventListener('mouseup', this.onDocumentPointerUp)
        this.stopFsScrub()
        clearTimeout(this.fsHideTimeout)
        if (this.stageResizeObserver) this.stageResizeObserver.disconnect()
    },

    methods: {
        formatTimecode,

        // Assigning width/height to a <canvas> wipes its backing store (per the HTML spec, even
        // when reassigned to the same values), so every size change has to be followed by a redraw
        // - and only *after* Vue has patched the attribute, or the redraw lands in the old backing
        // store and gets wiped a moment later. Opening the crop tool changes the size (see
        // cropWorkspaceSize) without going through MediaEditor.vue's refreshPreview, which is what
        // used to cover this: the frame went blank and stayed blank.
        //
        // Coalesced because the two things that trigger it - the size and the crop-preview clip -
        // change together on that same tick, and renderAt() is async: two overlapping calls can
        // interleave their draws (the race redrawOverlaysOnly documents avoiding).
        // Returns a promise resolving once the canvas actually holds the new frame - see
        // captureSnapshot, which has to read the pixels back.
        scheduleCanvasRedraw() {
            if (!this.pendingCanvasRedraw) {
                this.pendingCanvasRedraw = new Promise((resolve) => {
                    this.$nextTick(async () => {
                        // Cleared before awaiting, so anything that changes during the (async)
                        // render schedules its own follow-up rather than being swallowed by this one.
                        this.pendingCanvasRedraw = null
                        // The preview mode is read live off this component (see the player's
                        // getCropPreviewClipId), so this only has to trigger the draw.
                        if (this.player) await this.player.refresh()
                        resolve()
                    })
                })
            }
            return this.pendingCanvasRedraw
        },

        // The crop tool shows a full-source reference rather than the real composite (see
        // cropPreviewActive) and, in Remplir, in a source-shaped frame - so a snapshot taken while
        // it's open is neither cropped nor the right shape. Since MediaEditor.vue opens that tool on
        // startup, that's the normal case, not an edge one: the caller closes the tool first and
        // this waits for the resulting redraw, which needs both a Vue patch (the canvas element's
        // own width/height) and an async renderAt before the pixels are there to read.
        async captureSnapshot() {
            await this.scheduleCanvasRedraw()
            return this.getSnapshotDataUrl()
        },

        // One-off still capture of exactly what's on screen right now (video frame + overlay layer
        // already composited together, see renderOverlayLayer) - used by MediaEditor.vue's finish()
        // to freeze a representative thumbnail (including any stickers/text/crop already applied)
        // for the export-in-progress placeholder, without needing a second live-rendered canvas
        // running throughout the export (see that placeholder's own doc for why a live one was
        // dropped). null on failure (e.g. a tainted canvas from a non-CORS media source) - the
        // caller falls back to the plain origin video/image in that case.
        getSnapshotDataUrl() {
            try {
                return this.$refs.canvas.toDataURL('image/jpeg', 0.85)
            } catch (e) {
                console.error('[VideoPreviewCanvas] getSnapshotDataUrl failed', e)
                return null
            }
        },

        togglePlay() {
            if (!this.player) return
            if (this.playing) {
                this.player.pause()
            } else {
                this.player.play()
            }
            // play()/pause() both set player.playing synchronously before any await, so this
            // reflects the real state immediately rather than guessing ahead of it.
            this.playing = this.player.playing
        },

        seek(ms) {
            if (this.player) return this.player.seek(ms)
        },

        // Routed through the same coalescing as the internal triggers (see scheduleCanvasRedraw):
        // MediaEditor.vue's refreshPreview and this component's own displayCanvasSize watcher both
        // fire on the same tick for one edit (picking a ratio resizes the canvas *and* refreshes),
        // and two unawaited renderAt() calls can interleave their draws.
        refresh() {
            this.scheduleCanvasRedraw()
        },

        retryMedia(clipId, fileId) {
            if (this.player) this.player.retryMedia(clipId, fileId)
        },

        // "Axe de prévisualisation" (Timeline.vue's hover-preview toggle) - draws an arbitrary
        // frame without touching the player's real currentMs/onTimeUpdate, so hovering the
        // timeline previews a frame without moving the actual playhead. renderAt() already leaves
        // currentMs untouched on its own; clearPreview() just re-renders the real position to
        // wipe the preview back off once the hover ends.
        previewAt(ms) {
            if (this.player) this.player.renderAt(ms)
        },

        clearPreview() {
            if (this.player) this.player.renderAt(this.currentMs)
        },

        // Fullscreen the video stage itself (not the whole popup) - for demoing the actual output
        // without the surrounding editor chrome.
        toggleFullscreen() {
            const el = this.$refs.stage
            if (!el) return
            if (document.fullscreenElement) {
                document.exitFullscreen()
            } else if (el.requestFullscreen) {
                el.requestFullscreen()
            }
        },

        onFullscreenChange() {
            this.isFullscreen = document.fullscreenElement === this.$refs.stage
            if (this.isFullscreen) this.showFsControls()
        },

        showFsControls() {
            this.fsControlsVisible = true
            clearTimeout(this.fsHideTimeout)
            this.fsHideTimeout = setTimeout(() => { this.fsControlsVisible = false }, FS_CONTROLS_HIDE_MS)
        },

        onStageMouseMove() {
            if (this.isFullscreen) this.showFsControls()
        },

        onStageMouseLeave() {
            if (this.isFullscreen) this.fsControlsVisible = false
        },

        // "Contain" letterboxing in plain px against the stage's measured size.
        recomputeFrameSize() {
            // Measured here rather than read from the cached stageSize: the observer only reports
            // when the stage itself resizes, so any recompute triggered by something else (the
            // canvas changing shape, a gesture ending) could size the frame against a stale
            // measurement - and an over-large one makes the frame bigger than the stage it's
            // supposed to be contain-fit inside, which .vpc-stage's overflow then clips into a
            // picture that looks zoomed past its own bounds until the next gesture fixes it.
            const stage = this.$refs.stage
            if (!stage) return
            const stageWidth = stage.clientWidth
            const stageHeight = stage.clientHeight
            if (!stageWidth || !stageHeight) return
            const mediaRatio = this.displayCanvasSize.width / this.displayCanvasSize.height
            const stageRatio = stageWidth / stageHeight
            this.frameSize = stageRatio > mediaRatio
                ? { width: stageHeight * mediaRatio, height: stageHeight }
                : { width: stageWidth, height: stageWidth / mediaRatio }
        },

        onStagePointerDown() {
            this.isPointerDown = true
        },

        // CropSelector's corner handles use @mousedown.stop, so onStagePointerDown never sees them
        // and isPointerDown would stay false for the whole resize - leaving recomputeFrameSize free
        // to fire mid-gesture against deltas already scaled to the frame the drag started on.
        // onDocumentPointerUp clears it again on release, wherever that lands.
        onCropDragStart() {
            this.isDraggingCrop = true
            this.isPointerDown = true
        },

        // A click landing in the letterboxed margin (.vpc-stage outside .vpc-frame) never
        // reaches fabric's own canvas element at all, so its native "click on empty space
        // deselects" never fires there - this is the only thing that would.
        onStageClick() {
            if (this.$refs.overlayCanvas) this.$refs.overlayCanvas.selectOverlays([])
        },

        // Resizing the frame right when a gesture starts/ends is what broke fabric's own
        // mouse-to-canvas offset mid-drag (e.g. a selection-clearing click closing
        // OverlayConfigPanel and freeing up width) - resuming it only once released avoids that.
        onDocumentPointerUp() {
            if (!this.isPointerDown) return
            this.isPointerDown = false
            this.recomputeFrameSize()
        },

        startFsScrub(e) {
            this.fsScrubbing = true
            this.showFsControls()
            this.updateFsScrubFromEvent(e)
            window.addEventListener('mousemove', this.onFsScrubMove)
            window.addEventListener('mouseup', this.stopFsScrub)
        },

        onFsScrubMove(e) {
            if (!this.fsScrubbing) return
            this.showFsControls()
            this.updateFsScrubFromEvent(e)
        },

        stopFsScrub() {
            this.fsScrubbing = false
            window.removeEventListener('mousemove', this.onFsScrubMove)
            window.removeEventListener('mouseup', this.stopFsScrub)
        },

        // Not e.currentTarget: once the drag is tracked via window-level listeners (see
        // startFsScrub), currentTarget would be window itself instead of the progress bar.
        updateFsScrubFromEvent(e) {
            const track = this.$refs.fsProgress
            if (!track) return
            const rect = track.getBoundingClientRect()
            const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
            this.seek(Math.round(pct * this.durationMs))
        },

        // Fired continuously while an overlay is being dragged/resized/rotated - a lighter call
        // than refresh() (skips re-preloading every clip's media) since it just needs to keep the
        // baked-in overlay copy on this canvas in step with the live position on the interactive
        // fabric canvas sitting on top of it, not re-check the video sources themselves.
        onLiveOverlayTransform() {
            if (this.player) this.player.redrawOverlaysOnly()
        },

        startEditText({ overlayId, field, value, style }) {
            this.textEditor = { overlayId, field, style }
            this.textEditorValue = value
            this.$nextTick(() => {
                if (!this.$refs.textEditorInput) return
                this.$refs.textEditorInput.focus()
                this.$refs.textEditorInput.select()
            })
        },

        commitTextEdit() {
            if (!this.textEditor) return
            const { overlayId, field } = this.textEditor
            this.$emit('update-overlay-data', { overlayId, data: { [field]: this.textEditorValue } })
            this.textEditor = null
        },

        cancelTextEdit() {
            this.textEditor = null
        },

        selectOverlay(overlayId) {
            if (this.$refs.overlayCanvas) this.$refs.overlayCanvas.selectOverlay(overlayId)
        },

        selectOverlays(overlayIds) {
            if (this.$refs.overlayCanvas) this.$refs.overlayCanvas.selectOverlays(overlayIds)
        },

        getSelectedOverlayIds() {
            return this.$refs.overlayCanvas ? this.$refs.overlayCanvas.getSelectedOverlayIds() : []
        },

        hasActiveGesture() {
            return !!(this.$refs.overlayCanvas && this.$refs.overlayCanvas.hasActiveGesture())
        },

        onMultiSelectionChanged(payload) {
            this.multiSelection = payload
            // MediaEditor.vue has no other way to know a multi/group selection is active on the
            // canvas (selectedOverlayId stays null for one, same as "nothing selected") - without
            // this its hasOpenSidePanel stays false the whole time one is up, so the document-level
            // "click outside" listener never even attaches, and clicking anywhere outside the
            // canvas does nothing to deselect a currently-selected group. Passing the payload
            // through (not just a boolean) also lets ElementsTree.vue highlight which group is
            // currently selected - a second, independent readout of the same state the preview's
            // own frame is supposed to reflect, useful for telling apart "the selection itself is
            // wrong" from "the selection is right but the preview isn't rendering it".
            this.$emit('multi-selection-change', payload)
        },

        onToggleGroupClick() {
            if (!this.multiSelection) return
            if (this.multiSelection.isCompleteGroup) this.$emit('ungroup-selection', this.multiSelection.groupId)
            else this.$emit('group-selection', this.multiSelection.overlayIds)
        },

        // .vpc-frame's own rect, not .vpc-stage's - .vpc-stage merely centers it and can be
        // wider/taller than the actual video frame whenever the two don't share the same aspect
        // ratio (letterboxing), which would otherwise put every placement click/drag off by
        // however much padding surrounds the frame on that axis.
        placementPct(e) {
            const rect = this.$refs.frame.getBoundingClientRect()
            return {
                xPct: (e.clientX - rect.left) / rect.width,
                yPct: (e.clientY - rect.top) / rect.height
            }
        },

        onPlacementMove(e) {
            const { xPct, yPct } = this.placementPct(e)
            this.hoverSnap = computeSnap(xPct, yPct, this.overlays, this.currentMs, this.excludeOverlayId)
        },

        onPlacementLeave() {
            this.hoverSnap = { x: null, y: null }
        },

        onPlacementClick(e) {
            const { xPct, yPct } = this.placementPct(e)
            const snap = computeSnap(xPct, yPct, this.overlays, this.currentMs, this.excludeOverlayId)
            this.$emit('place', {
                xPct: snap.x !== null ? snap.x : xPct,
                yPct: snap.y !== null ? snap.y : yPct
            })
            this.hoverSnap = { x: null, y: null }
        }
    }
}
</script>

<style lang="scss" scoped></style>
